import React from "react";
import { widgetRefOf } from "../../../helpers/widgetRef";
import PropTypes from "prop-types";

import InfoTip from "./InfoTip";
import { Pick } from "./ProfitTrendSetup";
import {
  DEFAULT_NET_LABEL,
  MAX_NET_STEPS,
  NET_MODES,
  PAY_UNITS,
  defaultNetBonus,
  defaultNetSteps,
  netModeOf,
  netStepIssue,
  netStepSentence,
  payUnitOf,
  profitWidgetsOn,
  resolveProfitWidget,
  sanitizeNetBonus,
  usesAmount,
  usesShare,
} from "../../../components/NetProfitBonus/netBonus";
import "./customize.css";

/**
 * Setup for Net Profit Bonus: which Revenue, Expenses & Labor card it reads, what
 * earns the bonus (dollars, a margin, or both at once), and the ladder itself.
 *
 * The rows reuse the Build Your Own step bonus's editor styles (.gmbw-bstep…) so
 * the two "pay a bonus" editors are one thing to learn. The draft keeps every
 * field of every row while it is edited (`at`, `atPct`, `pay`), so flipping the
 * mode back and forth never loses what was typed; sanitizeNetBonus keeps only the
 * fields the chosen mode reads.
 */

const X = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const Plus = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const MODE_NOTE = {
  amount: "A step is reached once net profit hits its dollar line.",
  share: "A step is reached once net profit, as a share of revenue, hits its percent line.",
  both: "A step is reached only when net profit hits BOTH lines. Dollars alone can be hit by chasing revenue at a poor margin; a margin alone can be hit in a month too small to matter.",
};

/* Rows that carry no value for a line the new mode needs get the example's, so
   flipping to Percent or Both shows a real number to edit, not a blank that
   makes the whole ladder vanish until it is filled in. */
const fillFor = (steps, mode) => {
  const example = defaultNetSteps();
  return steps.map((s, i) => {
    const ex = example[i] || example[example.length - 1];
    return {
      ...s,
      at: usesAmount(mode) && (s.at === "" || s.at == null) ? ex.at : s.at,
      atPct: usesShare(mode) && (s.atPct === "" || s.atPct == null) ? ex.atPct : s.atPct,
    };
  });
};

const NetProfitBonusSetup = ({ draft, onChange, boardWidgets }) => {
  const bonus = draft.netBonus || defaultNetBonus();
  const mode = netModeOf(bonus);
  const rows = Array.isArray(bonus.steps) && bonus.steps.length ? bonus.steps : [];
  const team = bonus.per !== "player";

  const setBonus = (patch) => onChange({ ...draft, netBonus: { ...bonus, ...patch } });
  const setStep = (i, patch) => setBonus({ steps: rows.map((s, x) => (x === i ? { ...s, ...patch } : s)) });
  /* "$" is the default, so it is stored as nothing at all (the same convention
     the other bonus settings use) - a ladder that never touches the switch
     carries no payUnit and reads exactly as it did before percent pay existed. */
  const setPayUnit = (i, unit) => setBonus({
    steps: rows.map((s, x) => {
      if (x !== i) return s;
      const { payUnit, ...rest } = s;
      return unit === "%" ? { ...rest, payUnit: "%" } : rest;
    }),
  });
  const dropStep = (i) => setBonus({ steps: rows.filter((_, x) => x !== i) });
  const addStep = () => {
    const last = rows[rows.length - 1] || {};
    const at = Number(last.at) || 0;
    const atPct = Number(last.atPct) || 0;
    const pay = Number(last.pay) || 0;
    setBonus({
      steps: [...rows, {
        at: at > 0 ? Math.round((at * 1.5) / 100) * 100 : "",
        atPct: atPct > 0 ? Math.min(100, atPct + 5) : "",
        pay: pay > 0 ? pay * 2 : "",
        ...(last.payUnit === "%" ? { payUnit: "%" } : null),
      }],
    });
  };
  const setMode = (next) => {
    if (next === mode) return;
    setBonus({ mode: next, steps: fillFor(rows, next) });
  };

  const cards = profitWidgetsOn(boardWidgets);
  const options = cards.map((w, i) => ({
    value: widgetRefOf(w),
    label: w.title || `Revenue, Expenses & Labor${cards.length > 1 ? ` (${i + 1})` : ""}`,
  }));
  const picked = String(draft.profitWidgetId || (cards.length === 1 ? options[0].value : ""));

  const issue = netStepIssue({ ...bonus, steps: rows });
  const saved = sanitizeNetBonus({ ...bonus, steps: rows });
  const finished = !!saved;
  const untouched = !draft.netBonus;

  return (
    <>
      {/* ── Where Net profit comes from ─────────────────────────────────── */}
      <div className="gmbw-fld">
        <label>
          Paid on
          <InfoTip text="This card doesn't work out Net profit itself — it reads it from a Revenue, Expenses & Labor card on this dashboard, so the figure the bonus is paid on is exactly the Net profit on that card's tile." />
        </label>
        {options.length === 0 && (
          <p className="gmbw-hint">
            There is no Revenue, Expenses &amp; Labor card on this dashboard yet. Add one from the component
            library — this card reads its Net profit.
          </p>
        )}
        {options.length > 0 && (
          <>
            <Pick
              value={picked}
              placeholder="Which card"
              options={options}
              onChange={(v) => onChange({ ...draft, profitWidgetId: v })}
            />
            <p className="gmbw-hint">
              {options.length > 1
                ? "This dashboard has more than one and they can be set up differently — pick the one whose Net profit the bonus is paid on."
                : "Found on this dashboard. Its revenue, expenses and payroll are set up there, once."}
            </p>
          </>
        )}
      </div>

      {/* ── The ladder ──────────────────────────────────────────────────── */}
      <div className="gmbw-fld">
        <label>What earns the bonus</label>
        <div className="gmbw-bonusbox">
          <div className="gmbw-seg gmbw-bseg" role="radiogroup" aria-label="What earns the bonus">
            {NET_MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                role="radio"
                aria-checked={mode === m.id}
                className={`gmbw-segbtn${mode === m.id ? " on" : ""}`}
                onClick={() => setMode(m.id)}
              >
                {m.label}
              </button>
            ))}
          </div>
          <p className="gmbw-bnote">{MODE_NOTE[mode]}</p>

          {/* The ladder in words, above the boxes that set it - what the card
              will actually do, readable without decoding "$ & % pays $". */}
          {saved && (
            <ul className="gmbw-nbsum" aria-label="The ladder, in words">
              {saved.steps.map((st) => (
                <li key={`${st.at ?? ""}-${st.atPct ?? ""}`}>{netStepSentence(st, mode)}</li>
              ))}
            </ul>
          )}

          <div className="gmbw-bsteps">
            {rows.map((s, i) => (
              // eslint-disable-next-line react/no-array-index-key -- rows are positional while typing
              <div className="gmbw-nbstep" key={`nstep${i}`}>
                <div className="gmbw-nbstep-l">
                  <span className="gmbw-bstep-k">At</span>
                  {usesAmount(mode) && (
                    <label className="gmbw-bstep-in">
                      <i>$</i>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        inputMode="decimal"
                        aria-label={`Step ${i + 1} net profit`}
                        placeholder="20000"
                        value={s.at ?? ""}
                        onChange={(e) => setStep(i, { at: e.target.value })}
                      />
                    </label>
                  )}
                  {mode === "both" && <span className="gmbw-bstep-k">&amp;</span>}
                  {usesShare(mode) && (
                    <label className="gmbw-bstep-in">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="any"
                        inputMode="decimal"
                        aria-label={`Step ${i + 1} net margin`}
                        placeholder="10"
                        value={s.atPct ?? ""}
                        onChange={(e) => setStep(i, { atPct: e.target.value })}
                      />
                      <i>%</i>
                    </label>
                  )}
                </div>
                <div className="gmbw-nbstep-l">
                  <span className="gmbw-bstep-k">pays</span>
                  <div className="gmbw-unit" role="radiogroup" aria-label={`Step ${i + 1} pays in`}>
                    {PAY_UNITS.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        role="radio"
                        aria-checked={payUnitOf(s) === u.id}
                        aria-label={`Step ${i + 1} pays ${u.label}`}
                        title={u.label}
                        className={payUnitOf(s) === u.id ? "on" : ""}
                        onClick={() => setPayUnit(i, u.id)}
                      >
                        {u.id}
                      </button>
                    ))}
                  </div>
                  <label className="gmbw-bstep-in">
                    <input
                      type="number"
                      min="0"
                      max={payUnitOf(s) === "%" ? 100 : undefined}
                      step="any"
                      inputMode="decimal"
                      aria-label={`Step ${i + 1} pays`}
                      placeholder={payUnitOf(s) === "%" ? "1" : "250"}
                      value={s.pay ?? ""}
                      onChange={(e) => setStep(i, { pay: e.target.value })}
                    />
                    {/* Inside the box, like the % on the margin input, so every
                        pay box is the same width whichever unit it is in. */}
                    {payUnitOf(s) === "%" && <i>of revenue</i>}
                  </label>
                </div>
                <button
                  type="button"
                  className="gmbw-mrow-x"
                  aria-label={`Remove step ${i + 1}`}
                  disabled={rows.length <= 1}
                  onClick={() => dropStep(i)}
                >
                  <X />
                </button>
              </div>
            ))}
          </div>
          {rows.length < MAX_NET_STEPS ? (
            <button type="button" className="gmbw-addrow" onClick={addStep}>
              <Plus /> Add a step
            </button>
          ) : (
            <div className="gmbw-addrow full">{MAX_NET_STEPS} steps — that’s the max</div>
          )}
          {issue && <p className="gmbw-bnote" style={{ color: "#b42318" }}>{issue}</p>}
          {!issue && !finished && (
            <p className="gmbw-bnote" style={{ color: "#b42318" }}>Fill in at least one step — the card shows nothing until there is one.</p>
          )}
          <p className="gmbw-bnote">
            {untouched
              ? "These are example lines — change them to yours. "
              : ""}
            The highest step reached pays. Steps sort themselves low to high. A percent
            pay is that share of the period&apos;s total revenue, worked out live.
          </p>

          <label className="gmbw-blabel" htmlFor="gmbw-nb-label">What to call it on the card</label>
          <input
            id="gmbw-nb-label"
            className="gmbw-input"
            type="text"
            maxLength={60}
            value={bonus.label || ""}
            placeholder={DEFAULT_NET_LABEL}
            onChange={(e) => setBonus({ label: e.target.value })}
          />

          <label className="gmbw-blabel">Who it pays</label>
          <div className="gmbw-seg gmbw-bseg" role="radiogroup" aria-label="Who the bonus pays">
            <button
              type="button"
              role="radio"
              aria-checked={team}
              className={`gmbw-segbtn${team ? " on" : ""}`}
              onClick={() => setBonus({ per: "team" })}
            >
              One team pot
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={!team}
              className={`gmbw-segbtn${!team ? " on" : ""}`}
              onClick={() => setBonus({ per: "player" })}
            >
              Each player
            </button>
          </div>
          <p className="gmbw-bnote">
            Display only — the card shows what is being earned; nothing here pays anyone.
          </p>
        </div>
      </div>
    </>
  );
};

NetProfitBonusSetup.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  draft: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  boardWidgets: PropTypes.array,
};
NetProfitBonusSetup.defaultProps = { boardWidgets: [] };

export default NetProfitBonusSetup;

/** One line for Basics: what the card is reading and how many steps it pays. */
export const netBonusSummary = (opts, boardWidgets) => {
  const link = resolveProfitWidget(opts, boardWidgets);
  const bonus = sanitizeNetBonus(opts && opts.netBonus);
  let from = "pick a profit card";
  if (link.widget) from = `Net profit from "${link.widget.title || "Revenue, Expenses & Labor"}"`;
  else if (link.missing) from = "no Revenue, Expenses & Labor card on this board";
  const n = bonus ? bonus.steps.length : 0;
  return `${from} · ${n} step${n === 1 ? "" : "s"}`;
};
